import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DoorClosed, Lock, Wrench, Clock, Phone, Mail, MapPin, MessageCircle, Star, ChevronRight, ChevronDown, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { services, projects, testimonials, contactInfo, puertasTrastero, puertasVivienda, cerraduras } from '../mock';

const iconMap = {
  DoorClosed,
  Lock,
  Wrench,
  Clock
};

const Home = () => {
  const { toast } = useToast();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [googleReviews, setGoogleReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });

  // Fetch Google Reviews on mount
  useEffect(() => {
    const fetchGoogleReviews = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL || '';
        const response = await axios.get(`${backendUrl}/api/google-reviews`);
        setGoogleReviews(response.data.reviews || []);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching Google reviews:', error);
        // Fallback to mock testimonials if API fails
        setGoogleReviews(testimonials);
        setLoading(false);
      }
    };
    
    fetchGoogleReviews();
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL || '';
      
      // Send to backend
      const response = await axios.post(`${backendUrl}/api/contact`, formData);
      
      if (response.data.success) {
        setFormStatus({ 
          type: 'success', 
          message: '¡Solicitud enviada correctamente! Te contactaremos en menos de 24 horas.' 
        });
        
        // Reset form
        setFormData({ name: '', phone: '', email: '', service: '', message: '' });
      }
    } catch (error) {
      console.error('Error sending form:', error);
      
      // Manejar diferentes tipos de errores
      let errorMessage = 'Hubo un problema al enviar tu solicitud. Por favor, inténtalo de nuevo.';
      
      if (error.response) {
        if (error.response.status === 429) {
          errorMessage = 'Has enviado demasiadas solicitudes. Por favor, espera 10 minutos.';
        } else if (error.response.status === 422) {
          // Error de validación
          const detail = error.response.data?.detail;
          if (Array.isArray(detail) && detail.length > 0) {
            errorMessage = 'Por favor, revisa los datos del formulario. ' + detail[0]?.msg;
          } else {
            errorMessage = 'Por favor, revisa los datos del formulario.';
          }
        }
      }
      
      setFormStatus({ 
        type: 'error', 
        message: errorMessage 
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToContact = () => {
    document.getElementById('contacto').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="home-page">
      {/* Header */}
      <header className="site-header">
        <div className="header-container">
          <div className="logo">
            <img src="/logo.svg" alt="UK Cerrajeros" className="logo-icon" style={{ width: '40px', height: '40px' }} />
            <span className="logo-text">UK CERRAJEROS</span>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="nav-menu">
            <DropdownMenu>
              <DropdownMenuTrigger className="nav-link dropdown-trigger">
                Servicios <ChevronDown size={16} />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="dropdown-content">
                <DropdownMenuItem onClick={() => scrollToSection('puertas-vivienda')}>
                  Puertas para Vivienda
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => scrollToSection('puertas-trastero')}>
                  Puertas para Trastero (Novatea)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => scrollToSection('cerraduras')}>
                  Instalación de Cerraduras
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <a href="#nosotros" className="nav-link">Nosotros</a>
            <a href="#contacto" className="nav-link">Contacto</a>
          </nav>
          
          <a href={`https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}`} className="btn-primary desktop-only">
            <MessageCircle size={18} />
            WhatsApp
          </a>
          
          {/* Mobile Menu Button */}
          <button className="mobile-menu-button" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="mobile-menu">
            <div className="mobile-menu-items">
              <button onClick={() => { scrollToSection('puertas-vivienda'); setMobileMenuOpen(false); }} className="mobile-menu-item">
                Puertas para Vivienda
              </button>
              <button onClick={() => { scrollToSection('puertas-trastero'); setMobileMenuOpen(false); }} className="mobile-menu-item">
                Puertas para Trastero (Novatea)
              </button>
              <button onClick={() => { scrollToSection('cerraduras'); setMobileMenuOpen(false); }} className="mobile-menu-item">
                Instalación de Cerraduras
              </button>
              <a href="#nosotros" onClick={() => setMobileMenuOpen(false)} className="mobile-menu-item">
                Nosotros
              </a>
              <a href="#contacto" onClick={() => setMobileMenuOpen(false)} className="mobile-menu-item">
                Contacto
              </a>
              <a href={`https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}`} className="btn-primary mobile-whatsapp-btn">
                <MessageCircle size={18} />
                WhatsApp
              </a>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <img 
            src="https://images.unsplash.com/photo-1749477417968-2bc986bc6a42" 
            alt="UK Cerrajeros" 
            className="hero-image"
          />
          <div className="hero-overlay"></div>
        </div>
        <div className="hero-content">
          <h1 className="hero-title">SEGURIDAD, DISEÑO Y CONFIANZA EN CADA PUERTA</h1>
          <p className="hero-subtitle">UK Cerrajeros instala puertas y cerraduras de alta calidad con garantía total.</p>
          <Button onClick={scrollToContact} className="btn-primary btn-large">
            Solicita tu presupuesto gratuito
            <ChevronRight size={20} />
          </Button>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="services-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-label">NUESTROS SERVICIOS</span>
            <h2 className="section-title">Soluciones completas de seguridad</h2>
          </div>
          <div className="services-grid">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              const isNovatea = service.highlight === 'novatea';
              
              // Map service titles to section IDs
              const sectionMap = {
                'Puertas para Vivienda': 'puertas-vivienda',
                'Puertas para Trastero': 'puertas-trastero',
                'Instalación de Cerraduras': 'cerraduras'
              };
              
              const sectionId = sectionMap[service.title];
              const isClickable = !!sectionId;
              
              return (
                <Card 
                  key={service.id} 
                  className={`service-card ${isNovatea ? 'service-card-novatea' : ''} ${!isClickable ? 'service-card-info' : ''}`}
                  onClick={() => isClickable && scrollToSection(sectionId)}
                  style={{ cursor: isClickable ? 'pointer' : 'default' }}
                >
                  <div className="service-icon">
                    <Icon size={32} />
                  </div>
                  <h3 className="service-title">{service.title}</h3>
                  {isNovatea && <span className="novatea-badge">Novatea</span>}
                  <p className="service-description">{service.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Puertas Vivienda Section */}
      <section id="puertas-vivienda" className="puertas-vivienda-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-label">PUERTAS PARA VIVIENDA</span>
            <h2 className="section-title">Puertas de seguridad y acorazadas</h2>
            <p className="section-description">
              Puertas de seguridad y acorazadas para pisos, chalets y negocios. Instalación profesional con garantía total.
            </p>
          </div>
          <div className="puertas-grid">
            {puertasVivienda.map((puerta) => (
              <div key={puerta.id} className="puerta-card">
                <div className="puerta-image-wrapper">
                  <img src={puerta.image} alt={puerta.title} className="puerta-image" />
                </div>
                <div className="puerta-content">
                  <h3 className="puerta-title">{puerta.title}</h3>
                  <p className="puerta-description">{puerta.description}</p>
                  {puerta.features && (
                    <ul className="puerta-features">
                      {puerta.features.map((feature, index) => (
                        <li key={index} className="feature-item">
                          <span className="feature-check">✓</span> {feature}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Puertas Trastero Section - Novatea */}
      <section id="puertas-trastero" className="puertas-trastero-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-label">PUERTAS PARA TRASTERO</span>
            <div className="section-title-with-badge">
              <h2 className="section-title">Novatea - Puertas de acero antirobo</h2>
              <span className="novatea-badge-large">Novatea</span>
            </div>
            <p className="section-description">
              Puertas de seguridad para proteger todo lo que no puedes almacenar en casa
            </p>
          </div>
          <div className="trasteros-grid">
            {puertasTrastero.map((puerta) => (
              <div key={puerta.id} className={puerta.novatea ? "trastero-card" : "trastero-card-standard"}>
                <div className="trastero-image-wrapper">
                  <img src={puerta.image} alt={puerta.title} className="trastero-image" />
                  {puerta.novatea && (
                    <div className="novatea-overlay">
                      <span className="novatea-watermark">Novatea</span>
                    </div>
                  )}
                </div>
                <div className="trastero-content">
                  <h3 className="trastero-title">{puerta.title}</h3>
                  <p className="trastero-description">{puerta.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cerraduras Section */}
      <section id="cerraduras" className="cerraduras-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-label">INSTALACIÓN DE CERRADURAS</span>
            <h2 className="section-title">Sistemas de alta seguridad</h2>
            <p className="section-description">
              Cerraduras antibumping, antiganzúa y antitaladro. Configuración personalizada según tus necesidades.
            </p>
          </div>
          <div className="cerraduras-grid">
            {cerraduras.map((cerradura) => (
              <div key={cerradura.id} className="cerradura-card">
                <div className="cerradura-image-wrapper">
                  <img src={cerradura.image} alt={cerradura.title} className="cerradura-image" />
                </div>
                <div className="cerradura-content">
                  <h3 className="cerradura-title">{cerradura.title}</h3>
                  <p className="cerradura-description">{cerradura.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section - Google Reviews */}
      <section id="mantenimiento" className="testimonials-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-label">TESTIMONIOS</span>
            <h2 className="section-title">Opiniones de nuestros clientes</h2>
            <div className="google-rating-header">
              <div className="google-stars-small">
                <Star size={24} fill="#FBBC04" color="#FBBC04" />
                <Star size={24} fill="#FBBC04" color="#FBBC04" />
                <Star size={24} fill="#FBBC04" color="#FBBC04" />
                <Star size={24} fill="#FBBC04" color="#FBBC04" />
                <Star size={24} fill="#FBBC04" color="#FBBC04" />
              </div>
              <p className="google-rating-subtext">5.0 estrellas en Google • 12+ reseñas verificadas</p>
            </div>
          </div>
          
          <div className="testimonials-grid-new">
            {loading ? (
              <div className="loading-reviews">Cargando reseñas de Google...</div>
            ) : googleReviews.length > 0 ? (
              googleReviews.slice(0, 4).map((review, index) => (
                <div key={index} className="testimonial-card-new">
                  <div className="testimonial-header">
                    <div className="testimonial-author-info">
                      <div className="testimonial-avatar">
                        {review.author_name ? review.author_name.charAt(0) : 'C'}
                      </div>
                      <div>
                        <p className="testimonial-author-name">{review.author_name || 'Cliente'}</p>
                        <p className="testimonial-date">{review.relative_time_description || 'Hace tiempo'}</p>
                      </div>
                    </div>
                    <div className="google-verified-badge">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 0L9.798 2.829L13 3.619L10.5 6.618L11.166 10L8 8.829L4.834 10L5.5 6.618L3 3.619L6.202 2.829L8 0Z" fill="#4285F4"/>
                      </svg>
                      <span>Reseña de Google</span>
                    </div>
                  </div>
                  <div className="testimonial-rating-stars">
                    {[...Array(review.rating || 5)].map((_, i) => (
                      <Star key={i} size={16} fill="#FBBC04" color="#FBBC04" />
                    ))}
                  </div>
                  <p className="testimonial-text-new">{review.text || ''}</p>
                </div>
              ))
            ) : (
              testimonials.map((testimonial) => (
                <div key={testimonial.id} className="testimonial-card-new">
                  <div className="testimonial-header">
                    <div className="testimonial-author-info">
                      <div className="testimonial-avatar">
                        {testimonial.name.charAt(0)}
                      </div>
                      <div>
                        <p className="testimonial-author-name">{testimonial.name}</p>
                        <p className="testimonial-date">{testimonial.date}</p>
                      </div>
                    </div>
                    <div className="google-verified-badge">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 0L9.798 2.829L13 3.619L10.5 6.618L11.166 10L8 8.829L4.834 10L5.5 6.618L3 3.619L6.202 2.829L8 0Z" fill="#4285F4"/>
                      </svg>
                      <span>Reseña de Google</span>
                    </div>
                  </div>
                  <div className="testimonial-rating-stars">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={16} fill="#FBBC04" color="#FBBC04" />
                    ))}
                  </div>
                  <p className="testimonial-text-new">{testimonial.text}</p>
                </div>
              ))
            )}
          </div>
          
          <div className="google-reviews-footer">
            <a 
              href="https://www.google.com/maps/place/?q=place_id:ChIJG99qdWRcTg0Ru5GFsmzb7Dc" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Ver todas las reseñas en Google
              <ChevronRight size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="nosotros" className="about-section">
        <div className="section-container">
          <div className="about-content">
            <div className="about-text">
              <span className="section-label">SOBRE NOSOTROS</span>
              <h2 className="section-title">UK Cerrajeros y su equipo</h2>
              <p className="about-description">
                Con más de 20 años de experiencia en el sector, UK Cerrajeros ha liderado la instalación y mantenimiento de sistemas de puertas y cerrajería de alta seguridad en Uribe Kosta.
              </p>
              <p className="about-description">
                Nuestro equipo está especializado y en constante formación para ofrecer las soluciones más innovadoras del mercado. Trabajamos con los mejores fabricantes y utilizamos tecnología de vanguardia para ofrecer los mejores resultados.
              </p>
              <div className="about-values">
                <div className="value-item">
                  <h4 className="value-title">Seguridad</h4>
                  <p className="value-text">Sistemas certificados y probados</p>
                </div>
                <div className="value-item">
                  <h4 className="value-title">Profesionalismo</h4>
                  <p className="value-text">Equipo técnico especializado</p>
                </div>
                <div className="value-item">
                  <h4 className="value-title">Precisión</h4>
                  <p className="value-text">Instalación perfecta garantizada</p>
                </div>
              </div>
            </div>
            <div className="about-image">
              <img 
                src="https://images.pexels.com/photos/8985454/pexels-photo-8985454.jpeg" 
                alt="UK Cerrajeros equipo" 
                className="about-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="contact-section">
        <div className="section-container">
          <div className="contact-wrapper">
            <div className="contact-info">
              <span className="section-label">CONTACTO</span>
              <h2 className="section-title">Tu seguridad empieza con una puerta bien hecha</h2>
              <p className="contact-subtitle">Hablemos hoy. Asesoramiento gratuito y atención inmediata.</p>
              
              <div className="contact-details">
                <div className="contact-item">
                  <Phone className="contact-icon" />
                  <div>
                    <p className="contact-label">Teléfono</p>
                    <a href={`tel:${contactInfo.phone}`} className="contact-value">{contactInfo.phone}</a>
                  </div>
                </div>
                <div className="contact-item">
                  <MessageCircle className="contact-icon" />
                  <div>
                    <p className="contact-label">WhatsApp</p>
                    <a href={`https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}`} className="contact-value">{contactInfo.whatsapp}</a>
                  </div>
                </div>
                <div className="contact-item">
                  <Mail className="contact-icon" />
                  <div>
                    <p className="contact-label">Email</p>
                    <a href={`mailto:${contactInfo.email}`} className="contact-value">{contactInfo.email}</a>
                  </div>
                </div>
                <div className="contact-item">
                  <MapPin className="contact-icon" />
                  <div>
                    <p className="contact-label">Dirección</p>
                    <p className="contact-value">{contactInfo.address}</p>
                  </div>
                </div>
              </div>
            </div>

            <Card className="contact-form-card">
              <h3 className="form-title">Solicita tu presupuesto gratuito</h3>
              <form onSubmit={handleSubmit} className="contact-form">
                <Input
                  type="text"
                  name="name"
                  placeholder="Nombre completo"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
                <Input
                  type="tel"
                  name="phone"
                  placeholder="Teléfono"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
                <Input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  required
                  className="form-select"
                >
                  <option value="">Selecciona un servicio</option>
                  <option value="puertas-vivienda">Puertas vivienda</option>
                  <option value="puertas-trastero">Puertas trastero</option>
                  <option value="mejora-seguridad">Mejora de seguridad</option>
                  <option value="mantenimiento-reparacion">Mantenimiento / Reparación</option>
                  <option value="copias-llave">Copias llave protegida</option>
                </select>
                <Textarea
                  name="message"
                  placeholder="Cuéntanos sobre tu proyecto..."
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  className="form-textarea"
                />
                
                {formStatus.message && (
                  <div className={`form-status ${formStatus.type === 'success' ? 'form-status-success' : 'form-status-error'}`}>
                    {formStatus.type === 'success' ? '✓' : '✕'} {formStatus.message}
                  </div>
                )}
                
                <button type="submit" className="btn-primary btn-full" disabled={isSubmitting}>
                  {isSubmitting ? 'Enviando...' : 'Enviar solicitud'}
                  {!isSubmitting && <ChevronRight size={20} />}
                </button>
              </form>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="site-footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo.svg" alt="UK Cerrajeros" className="logo-icon" style={{ width: '40px', height: '40px' }} />
                <span className="logo-text">UK CERRAJEROS</span>
              </div>
              <p className="footer-tagline">Seguridad y confianza en cada instalación</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4 className="footer-title">Servicios</h4>
                <ul className="footer-list">
                  <li><a href="#servicios">Instalación</a></li>
                  <li><a href="#servicios">Mantenimiento</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4 className="footer-title">Empresa</h4>
                <ul className="footer-list">
                  <li><a href="#nosotros">Sobre nosotros</a></li>
                  <li><a href="#proyectos">Proyectos</a></li>
                  <li><a href="#contacto">Contacto</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4 className="footer-title">Horario</h4>
                <p className="footer-schedule">{contactInfo.schedule}</p>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 UK Cerrajeros. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
